'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { trackAnalyticsEvent } from '@/lib/analytics';
import { useUserProfile } from '@/hooks/useUserProfile';
import { resolveUtmTrackingParams } from '@/lib/url-utils';

export const useUnloadTrack = () => {
    const pathname = usePathname();
    const { data: profileData } = useUserProfile();

    useEffect(() => {
        // Only track on subscription page (/) and auth pages (/auth/*)
        // Avoid tracking on /download-app
        const shouldTrack = pathname === '/' || pathname?.startsWith('/auth');
        if (!shouldTrack) return;

        // Check if user is already premium to avoid annoying premium users
        const user = profileData?.user;
        const isPremium = user?.isPremiumPlan || !!user?.plan?.historyId;

        if (isPremium) return;

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            // Only show dialog if payment was explicitly initiated but not completed
            const isPaymentInitiated = sessionStorage.getItem('paymentInitiated') === 'true';
            if (!isPaymentInitiated) return;

            // We only show dialog if we at least have a user ID or phone number to identify them
            if (!user?._id && !user?.phoneNumber) return;

            // Show the standard browser "Leave site?" dialog
            e.preventDefault();
            e.returnValue = ''; // Setting returnValue triggers the browser dialog
            return '';
        };

        const handlePageHide = () => {
            // Only track if payment was explicitly initiated but not completed
            const isPaymentInitiated = sessionStorage.getItem('paymentInitiated') === 'true';
            if (!isPaymentInitiated) return;

            // We only fire if we at least have a user ID or phone number to identify them
            if (!user?._id && !user?.phoneNumber) return;

            // Track the user info
            const eventData: Record<string, string> = {
                page: pathname,
                timestamp: new Date().toISOString()
            };

            if (user) {
                eventData.user_id = user._id;
                if (user.phoneNumber) eventData.phoneNumber = user.phoneNumber;
                if (user.email) eventData.email = user.email;
                if (user.fullName) eventData.fullName = user.fullName;
            }

            Object.assign(eventData, resolveUtmTrackingParams());

            // Fire event to MoEngage
            // Note: Modern analytics SDKs handle page exit tracking via sendBeacon internally
            trackAnalyticsEvent('USER_LEFT_BEFORE_SUBSCRIBING', eventData);
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('pagehide', handlePageHide);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('pagehide', handlePageHide);
        };
    }, [pathname, profileData]);
};
